import { CheckCircleOutlined, CloseCircleOutlined, EyeOutlined } from '@ant-design/icons'
import { Button, Flex } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import PreviewShipmentBlank from '../../../../components/buttons/preview-shipment-blank/preview-shipment-blank'
import ColorProofNumber from '../../../../components/color-proof-number/color-proof-number'
import StampImageLink from '../../../../components/stamp-image-link/stamp-image-link'
import { renderShipmentCalculatedValue } from '../../../../components/shipment-calculated-value/render-shipment-calculated-value'
import formatNumber from '../../../../helpers/formatters/format-any-number'
import formatDuration from '../../../../helpers/date-time/format-duration'
import { formatRollerDisplay } from '../../../../helpers/format-roller-display'
import renderDate from '../../../../helpers/tables/render-date'
import { MaterialRequirement } from '../../../../types/api/components/material/material-requirements'
import PrintQueueEntry from '../../../../types/api/print-queue-entry'
import ShipmentPosition from '../../../../types/api/shipment-position'
import VirtualStampNumber from '../../../calculations/components/calculation-form/components/other/virtual-stamp-number'
import { MaterialColumnCell } from '../../../schedules/flexo/components/cells/material-column-cell'
import PrintJobNoteCell from '../../../schedules/flexo/components/cells/print-job-note-cell'
import { createAvailabilityColumns } from '../../../schedules/flexo/constants/availability-columns'
import {
    createAdditionalInfoColumn,
    createCuratorPrintColumn,
} from '../../../schedules/flexo/constants/columns'
import DownloadHonestSignsCodesFile from '../../../../components/buttons/download-honest-signs-codes-file/download-honest-signs-codes-file'
import ActionsCell from './components/actions-cell'
import { RotatedTitle } from '../../../../components/rotated-title/rotated-title'

export const getColumns = (
    canEdit: boolean,
    isRgMachine = false
): ColumnsType<PrintQueueEntry> => [
    {
        dataIndex: ['print_job', 'id'],
        title: 'Номер печати',
        width: 90,
        render: (id, record: PrintQueueEntry) => (
            <ColorProofNumber shipmentType={record.print_job?.shipment?.shipmentType}>
                {id}
            </ColorProofNumber>
        ),
    },
    {
        dataIndex: ['print_job', 'shipment', 'clientDeliveryDate'],
        title: 'Дата поставки ',
        width: 120,
        render: renderDate,
    },
    { dataIndex: ['print_job', 'shipment', 'client', 'name'], title: 'Клиент' },
    createAdditionalInfoColumn<PrintQueueEntry>(record => record.print_job?.shipment),
    createCuratorPrintColumn<PrintQueueEntry>(record => record.print_job?.shipment),
    {
        dataIndex: ['print_job', 'shipment', 'shipment_positions'],
        title: 'Тираж',
        render(v: ShipmentPosition[]) {
            return formatNumber((v ?? []).reduce((acc, v) => acc + (v?.copies ?? 0), 0))
        },
    },
    {
        dataIndex: ['print_job', 'shipment', 'materialRequirements'],
        title: 'Материал',
        width: 100,
        render(v: MaterialRequirement[], record: PrintQueueEntry) {
            return (
                <MaterialColumnCell value={v} shipment={record.print_job?.shipment} />
            )
        },
    },
    {
        dataIndex: ['print_job', 'shipment', 'calculated', 'materialLength'],
        title: 'Метраж',
        width: 70,
        render(_v, r: PrintQueueEntry) {
            return renderShipmentCalculatedValue(r.print_job?.shipment, calculated =>
                Math.round((calculated?.materialLength || 0) * 1000) / 1000
            )
        },
    },
    {
        dataIndex: ['print_job', 'shipment', 'stamp'],
        title: 'Штамп / тубус',
        render(_, record) {
            const shipment = record.print_job?.shipment
            if (!shipment) return '—'

            if (shipment.withoutCutting) {
                const virtual = shipment.virtualStamp
                const sizes =
                    virtual?.width != null && virtual?.length != null
                        ? ` (${virtual.width}x${virtual.length})`
                        : ''
                return (
                    <>
                        <VirtualStampNumber data={shipment} />
                        {sizes}
                    </>
                )
            }

            const stamp = shipment.stamp
            if (!stamp) return '—'

            const sizes =
                stamp.width != null && stamp.length != null
                    ? ` (${stamp.width}x${stamp.length})`
                    : ''

            return (
                <Flex gap={4} align={'center'} wrap={'wrap'}>
                    <span>{stamp.number || '—'}</span>
                    <StampImageLink stamp={stamp} />
                    <span>{`/ ${stamp.tube?.number || 'нет'}${sizes}`}</span>
                </Flex>
            )
        },
    },
    {
        title: 'Вал',
        render(_, record) {
            const shipment = record.print_job?.shipment
            if (!shipment) return '—'

            return formatRollerDisplay(
                shipment.withoutCutting
                    ? shipment.virtualStamp?.roller?.roller
                    : shipment.stamp?.roller?.roller
            )
        },
    },
    createAvailabilityColumns<PrintQueueEntry>(record => record.print_job?.shipment),
    // TODO: рассчитываемое поле, добавить
    {
        dataIndex: ['print_job', 'shipment', 'calculated', 'commonTime'],
        title: <RotatedTitle>Время тиража</RotatedTitle>,
        width: 100,
        render(_v, r: PrintQueueEntry) {
            return renderShipmentCalculatedValue(r.print_job?.shipment, calculated =>
                formatDuration(calculated?.commonTime || 0)
            )
        },
    },
    {
        dataIndex: 'preparation',
        title: 'Подготовка',
        width: 85,
        render(v, r) {
            const prepared = r.stampPrepared && r.formsPrepared && r.blankPrepared

            return prepared ? (
                <CheckCircleOutlined style={{ color: 'green', fontSize: '18px' }} />
            ) : (
                <CloseCircleOutlined style={{ color: 'red', fontSize: '18px' }} />
            )
        },
    },
    {
        dataIndex: 'blank',
        title: 'Бланк',
        width: isRgMachine ? 220 : 50,
        render(v, r) {
            const shipment = r.print_job?.shipment
            if (!shipment) {
                return '-'
            }

            const hasHonestSignTask = Boolean(
                shipment.tasks?.some(task => task.type === 'Честный знак')
            )

            return (
                <Flex gap={4} align="center" wrap="wrap">
                    <PreviewShipmentBlank
                        shipment={shipment}
                        blankType="production"
                        blankPreviewFiles={shipment.blankPreviewFiles}
                    >
                        <Button
                            title="Посмотреть бланк"
                            size="small"
                            icon={<EyeOutlined />}
                            color="magenta"
                            variant="solid"
                        />
                    </PreviewShipmentBlank>
                    {isRgMachine && hasHonestSignTask ? (
                        <DownloadHonestSignsCodesFile files={shipment.honestSignsCodesFile} />
                    ) : null}
                </Flex>
            )
        },
    },

    // {
    //     dataIndex: 'cards',
    //     title: 'Тех. карты',
    //     width: 110,
    //     render(v, r) {
    //         return (
    //             <Popover
    //                 title={
    //                     'Открывается раздел тех карт с предзаполенеными фильтрами по клиенту, имени этикетки и пр'
    //                 }
    //             >
    //                 <Link to={routes.technicalCards}>
    //                     <Button type={'link'} icon={<HistoryOutlined />}>
    //                         Смотреть
    //                     </Button>
    //                 </Link>
    //             </Popover>
    //         )
    //     },
    // },
    {
        title: 'Примечание',
        width: 140,
        render(_, record) {
            return <PrintJobNoteCell printJob={record.print_job!} editable={canEdit} />
        },
    },
    {
        dataIndex: 'actions',
        title: '',
        render(v, r) {
            return <ActionsCell printQueueEntry={r} />
        },
    },
]
